import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

export default function CustomAudioPlayer({ src }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        let animationFrameId;
        const updateTime = () => {
            if (audio && !audio.paused) {
                setCurrentTime(audio.currentTime);
                animationFrameId = requestAnimationFrame(updateTime);
            }
        };

        const onPlay = () => {
            setIsPlaying(true);
            animationFrameId = requestAnimationFrame(updateTime);
        };
        const onPause = () => {
            setIsPlaying(false);
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onEnded = () => setIsPlaying(false);

        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', onEnded);

        audio.load();

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', onEnded);
        };
    }, [src]);

    const togglePlay = () => {
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    };

    const handleSeek = (e) => {
        const time = Number(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const handleSpeedChange = () => {
        const rates = [1, 1.5, 2, 3];
        const nextRate = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
        audioRef.current.playbackRate = nextRate;
        setPlaybackRate(nextRate);
    };

    const formatTime = (time) => {
        if (!time || isNaN(time)) return '0:00';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            width: '100%', boxSizing: 'border-box',
            padding: '10px 14px' // internal padding so controls don't touch the edges
        }}>
            <audio ref={audioRef} src={src} preload="metadata" />

            <button 
                onClick={togglePlay}
                style={{
                    background: 'transparent', color: '#54656f',
                    border: 'none', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0
                }}
            >
                {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
            </button>
            
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input 
                        type="range" 
                        min="0" 
                        max={duration || 0} 
                        value={currentTime} 
                        onChange={handleSeek}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            height: '4px',
                            accentColor: '#10b981',
                            cursor: 'pointer',
                            margin: 0
                        }}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#667781', fontVariantNumeric: 'tabular-nums' }}>
                        {formatTime(isPlaying ? currentTime : duration)}
                    </span>
                    <button 
                        onClick={handleSpeedChange}
                        style={{
                            background: '#e9edef', border: 'none', color: '#54656f',
                            borderRadius: '12px', padding: '2px 8px', fontSize: '10px', 
                            fontWeight: '600', cursor: 'pointer',
                        }}
                    >
                        {playbackRate}x
                    </button>
                </div>
            </div>
        </div>
    );
}
